import clsx from 'clsx';
import styles from './tableWithFixedHeader.module.scss';
import { FC, forwardRef, PropsWithChildren } from 'react';
import { buildTestId } from 'src/helpers/test-helpers';
import { flexRender, Header as TanHeader, Cell, Row } from '@tanstack/react-table';
import { ReactComponent as ArrowIcon } from 'src/assets/img/common/arrow_24.svg';

const TableLayoutRoot: FC<
  PropsWithChildren<{
    testId?: string;
    className?: string;
    columnCount: number;
    firstColumnWidth?: string;
    scrollableColumnsTemplate?: string;
  }>
> = ({ children, testId, className, columnCount, firstColumnWidth, scrollableColumnsTemplate }) => (
  <div
    data-test-id={buildTestId(testId, 'app-table')}
    className={clsx(styles.table, className)}
    style={
      {
        '--col-count': columnCount,
        '--first-col-width': firstColumnWidth,
        '--scrollable-cols-template': scrollableColumnsTemplate,
      } as React.CSSProperties
    }
  >
    {children}
  </div>
);

const Header: FC<PropsWithChildren> = ({ children }) => <div className={styles.headerContainer}>{children}</div>;

const HeaderFirstColumn: FC<PropsWithChildren> = ({ children }) => (
  <div className={styles.headerFirstColumn}>{children}</div>
);

const HeaderScrollableColumns = forwardRef<HTMLDivElement, PropsWithChildren>(({ children }, ref) => (
  <div className={styles.headerScrollable} ref={ref}>
    {children}
  </div>
));

const Body: FC<PropsWithChildren> = ({ children }) => <div className={styles.bodyContainer}>{children}</div>;

const BodyFirstColumn = forwardRef<HTMLDivElement, PropsWithChildren<{ hasHorizontalScroll?: boolean }>>(
  ({ children, hasHorizontalScroll }, ref) => (
    <div className={clsx(styles.bodyFirstColumn, { [styles.hasHorizontalScroll]: hasHorizontalScroll })} ref={ref}>
      {children}
    </div>
  ),
);

const BodyScrollableColumns = forwardRef<
  HTMLDivElement,
  PropsWithChildren<{ onScroll?: (event: React.UIEvent<HTMLDivElement>) => void }>
>(({ children, onScroll }, ref) => (
  <div className={styles.bodyScrollable} ref={ref} onScroll={onScroll}>
    {children}
  </div>
));

export const TableLayout = Object.assign(TableLayoutRoot, {
  Table: Object.assign(TableLayoutRoot, {
    Header: Object.assign(Header, {
      FirstColumn: HeaderFirstColumn,
      ScrollableColumns: HeaderScrollableColumns,
    }),
    Body: Object.assign(Body, {
      FirstColumn: BodyFirstColumn,
      ScrollableColumns: BodyScrollableColumns,
    }),
  }),
});

export const HeaderCell = <D extends object>({ header }: { header: TanHeader<D, unknown> }) => {
  return (
    <div key={header.id} className={styles.headerCell}>
      {flexRender(header.column.columnDef.header || '\u00A0', header.getContext())}
    </div>
  );
};

export const TableCell = <D extends object>({ cell }: { cell: Cell<D, unknown> }) => {
  const meta = cell.column.columnDef.meta;

  return (
    <div key={cell.id} data-colored={meta?.isColored} className={styles.tableCell}>
      {flexRender(cell.column.columnDef.cell || '\u00A0', cell.getContext())}
    </div>
  );
};

export const GroupedRow = <D extends object>({
  children,
  row,
  hideGroupingValue = false,
  contentWidth,
}: {
  children: (cell: Cell<D, unknown>[]) => React.ReactNode;
  row: Row<D>;
  hideGroupingValue?: boolean;
  contentWidth?: string;
}) => {
  if (!row.getIsGrouped())
    return (
      <TableRow key={row.id} row={row}>
        {children}
      </TableRow>
    );

  return (
    <div className={styles.groupedRow} style={contentWidth ? { minWidth: contentWidth } : undefined}>
      <button
        className={styles.button}
        onClick={() => row.toggleExpanded()}
        style={contentWidth ? { minWidth: contentWidth } : undefined}
      >
        {!hideGroupingValue && (
          <div className={styles.groupingValueContainer}>
            <div className={styles.groupingValueText}>{row.groupingValue as string}</div>
            <ArrowIcon className={clsx(styles.icon, { [styles.expendedIcon]: row.getIsExpanded() })} />{' '}
          </div>
        )}
      </button>
      {row.getIsExpanded() && (
        <div className={styles.subRowsContainer}>
          {row.subRows.map((subRow) => (
            <TableRow key={subRow.id} row={subRow}>
              {children}
            </TableRow>
          ))}
        </div>
      )}
    </div>
  );
};

export const TableRow = <D extends object>({
  row,
  children,
}: {
  row: Row<D>;
  children: (cell: Cell<D, unknown>[]) => React.ReactNode;
}) => <div className={styles.tableRow}>{children(row.getVisibleCells())}</div>;
